// Fase 3: Página pública para activación de perfiles pre-cargados
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

interface TherapistData {
  id: string;
  bio: string | null;
  hourly_rate: number | null;
  currency: string | null;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
}

export default function ActivateProfile() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [therapistData, setTherapistData] = useState<TherapistData | null>(null);
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    hourly_rate: 50,
    currency: 'USD',
    password: '',
    confirmPassword: '',
    accept_terms: false
  });

  useEffect(() => {
    if (token) {
      loadTherapistData();
    }
  }, [token]);

  const loadTherapistData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Llamar a la función RPC para obtener datos del terapeuta
      const { data: therapistRaw, error: therapistError } = await supabase
        .rpc('get_therapist_by_token', { p_token: token }) as { data: any, error: any };

      if (therapistError) {
        console.error('Error fetching therapist:', therapistError);
        setError('Token de activación inválido o expirado');
        return;
      }

      if (!therapistRaw || !Array.isArray(therapistRaw) || therapistRaw.length === 0) {
        setError('Token de activación inválido o expirado');
        return;
      }

      const rawData = therapistRaw[0];
      
      const therapistData: TherapistData = {
        id: rawData.id,
        bio: rawData.bio,
        hourly_rate: rawData.hourly_rate,
        currency: rawData.currency,
        email: rawData.email,
        first_name: rawData.first_name,
        last_name: rawData.last_name,
        phone: rawData.phone
      };

      setTherapistData(therapistData);
      setFormData(prev => ({
        ...prev,
        first_name: therapistData.first_name || '',
        last_name: therapistData.last_name || '',
        phone: therapistData.phone || '',
        bio: therapistData.bio || '',
        hourly_rate: therapistData.hourly_rate || 50,
        currency: therapistData.currency || 'USD'
      }));
    } catch (err: any) {
      setError('Error cargando datos del perfil');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: 'Error',
          description: 'Las contraseñas no coinciden',
          variant: 'destructive'
        });
        return;
      }

      if (!formData.accept_terms) {
        toast({
          title: 'Error',
          description: 'Debe aceptar los términos y condiciones',
          variant: 'destructive'
        });
        return;
      }

      setSubmitting(true);

      const { data, error } = await supabase.functions.invoke('activate-therapist-profile', {
        body: {
          token,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          bio: formData.bio,
          hourly_rate: formData.hourly_rate,
          currency: formData.currency,
          accept_terms: formData.accept_terms
        }
      });

      if (error) throw error;

      toast({
        title: '¡Éxito!',
        description: 'Su perfil ha sido activado exitosamente',
      });

      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        navigate('/auth?message=profile_activated');
      }, 2000);

    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error activando el perfil',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!confirm('¿Está seguro que desea rechazar esta invitación? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setSubmitting(true);

      const { error } = await supabase.functions.invoke('reject-therapist-profile', {
        body: {
          token,
          reason: 'Usuario rechazó la invitación',
          delete_data: true
        }
      });

      if (error) throw error;

      toast({
        title: 'Perfil rechazado',
        description: 'Su perfil ha sido eliminado de nuestra plataforma',
      });

      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error rechazando el perfil',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="h-6 w-6" />
              <CardTitle>Error</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!therapistData) return null;

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Active su perfil profesional</CardTitle>
            <CardDescription>
              Hemos pre-cargado su información profesional en Global Therapy Hub. 
              Complete los datos faltantes para activar su perfil.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Información pre-cargada */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Email: <strong>{therapistData.email}</strong>
              </AlertDescription>
            </Alert>

            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Nombre *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Apellido *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Biografía profesional</Label>
                  <Textarea
                    id="bio"
                    rows={6}
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Cuéntenos sobre su experiencia y enfoque terapéutico..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hourly_rate">Tarifa por hora *</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      min="0"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">Moneda</Label>
                    <Input
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    />
                  </div>
                </div>

                <Button onClick={() => setStep(2)} className="w-full">
                  Continuar
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password">Crear contraseña *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mínimo 8 caracteres"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmar contraseña *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={formData.accept_terms}
                    onCheckedChange={(checked) => setFormData({ ...formData, accept_terms: checked as boolean })}
                  />
                  <Label htmlFor="terms" className="text-sm cursor-pointer">
                    Acepto los términos y condiciones de Global Therapy Hub y confirmo que la información proporcionada es correcta
                  </Label>
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Atrás
                  </Button>
                  <Button 
                    onClick={handleActivate} 
                    disabled={submitting || !formData.accept_terms}
                    className="flex-1"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Activando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Activar perfil
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Opción de rechazar */}
            <div className="pt-6 border-t">
              <Button
                variant="ghost"
                onClick={handleReject}
                disabled={submitting}
                className="w-full text-muted-foreground"
              >
                No estoy interesado - Eliminar mi perfil
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
